import * as React from 'react';
import {
  Component,
  Expressions,
  Types,
  type Type,
  VariableMap,
  EnvTypes,
} from '@packages/systems/wfdl';
import type {Expression} from '@packages/systems/wfdl-ast';
import {
  ContextualExpressions,
  ContextualExpression,
  stripAnn,
  getAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  ExpressionEditorDispatch,
  extendExpressionEditorActionAtPath,
  getBindingLabel,
  getFieldId,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {
  Button,
  CloseDefaultIcon,
  ConditionalRuleIcon,
  HStack,
  IconButton,
  Text,
  UnlinkIcon,
  View,
  VStack,
  ArrowSwooshUpRightIcon,
  Divider,
} from '@packages/systems/spring';
import {
  getTopBarHeight,
  rightSidebarWidth,
} from '@packages/systems/design-system/constants';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';
import {emotionStyled} from '@packages/utilities/styled';
import {GetPathForDispatch} from './types';
import {bindChangedAt} from './utils';
import {
  getAutoOpenConditionalPath,
  conditionalClosed,
  conditionalOpenedAt,
} from '@packages/systems/conditionals';
import {deconstructConfiguration} from './ConditionalConfiguration';
import {
  getCleanedConditionalDispatchValue,
  getConditionalMode,
  isConditionalReferenceCE,
  shouldHideSimpleConditionalPreviewFallbackText,
  shouldKeepMiniSettingsOpen,
} from '@packages/systems/plugins-utils/Conditionals';
import {DataTypeSummary} from '@packages/systems/ExpressionEditor-utils-ui';
import {resolveSlotBindingLabel} from '@packages/utilities/platform';
import {selectCurrentComponent} from '@packages/utilities/platform-selectors';
import {Viewport} from '@packages/systems/ui/overlay';
import {
  DesignerStores,
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';
import {ViewportState} from '@packages/systems/designer/viewport';
import {Box} from '@packages/utilities/box';
import SelectButton from '@packages/systems/design-system/SelectButton';
import {Grid} from '@packages/systems/design-system/Grid';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {createSelector} from 'reselect';
import {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';

const selectStyleBlockState = (state: DesignerStores) => state.StyleBlockStore;

const Separator = emotionStyled('div')({
  width: '100%',
  height: '$2',
});

const ArrowSwooshDownRight = emotionStyled(ArrowSwooshUpRightIcon)({
  transform: 'rotate(90deg)',
  marginRight: '$1',
  color: '$textSecondary',
});

const CONDITIONAL_SETTINGS_WIDTH = rightSidebarWidth * 2;

const ConditionalSettingsWrapper = emotionStyled('div')({
  width: CONDITIONAL_SETTINGS_WIDTH,
});

const ConfirmationWrapper = emotionStyled('div')({
  width: rightSidebarWidth,
});

const ConditionalSettingsBody = emotionStyled('div')({
  paddingLeft: '$2',
  paddingRight: '$2',
  paddingBottom: '$2',
  maxHeight: '75vh',
  overflowY: 'auto',
});

type ConditionalAnalysis =
  | {status: 'complete'}
  | {status: 'incomplete'; cleanedArg: Expression}
  | {status: 'empty'};

function analyzeConditionalForCleanup(
  configExpr: ContextualExpression
): ConditionalAnalysis {
  const strippedConfig = stripAll(configExpr);
  if (Expressions.fromHoleyExpression(strippedConfig) !== null) {
    return {status: 'complete'};
  }

  const result = getCleanedConditionalDispatchValue(strippedConfig);
  if (result.tag === 'Conditional') {
    const conditionalCall = Expressions.getCall(result.val);
    if (conditionalCall) {
      return {status: 'incomplete', cleanedArg: conditionalCall.arg};
    }
  }

  return {status: 'empty'};
}

interface ConfirmationContentProps {
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmationContent = ({
  onCancel,
  onConfirm,
}: ConfirmationContentProps) => (
  <ConfirmationWrapper data-automation-id="conditional-settings-delete-confirmation">
    <VStack spacing={0}>
      <HStack justify="space-between">
        <Text font="heading" padding="$1 $1 $1 $2">
          Delete conditional?
        </Text>
        <IconButton
          Icon={CloseDefaultIcon}
          label="Close"
          variant="ghost"
          onClick={onCancel}
        />
      </HStack>
      <Divider />
      <Text colorVariant="secondary" padding="$2">
        All conditions associated with this conditional will be permanently
        deleted.
      </Text>
      <Divider />
      <HStack padding="$2" justify="end">
        <Button
          data-automation-id="conditional-settings-delete-cancel-button"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          colorVariant="danger"
          data-automation-id="conditional-settings-delete-confirm-button"
          onClick={onConfirm}
        >
          Delete
        </Button>
      </HStack>
    </VStack>
  </ConfirmationWrapper>
);

interface ConditionalSettingsContentProps {
  configExpr: ContextualExpression | null;
  type: Type | null;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
  onDeleteClick: () => void;
}

const ConditionalSettingsContent = ({
  configExpr,
  type,
  dispatch,
  getPathForDispatch,
  onDeleteClick,
}: ConditionalSettingsContentProps) => {
  if (!configExpr) return null;

  const settingLabel = type ? Types.getLabel(type) : '';

  return (
    <ConditionalSettingsWrapper>
      <HStack
        justify="space-between"
        paddingTop="$1"
        paddingBottom="$1"
        paddingLeft="$2"
        paddingRight="$1"
      >
        <Text font="heading">{settingLabel} conditions</Text>
        <IconButton
          Icon={UnlinkIcon}
          label="Delete conditional"
          variant="ghost"
          withTooltip
          onClick={onDeleteClick}
        />
      </HStack>
      <ConditionalSettingsBody>
        <ExpressionEditor
          expression={configExpr}
          dispatch={extendExpressionEditorActionAtPath(dispatch)(
            getPathForDispatch(configExpr)
          )}
        />
      </ConditionalSettingsBody>
    </ConditionalSettingsWrapper>
  );
};

export function ConditionalSettings({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
}) {
  const {env} = useExpressionEditorImplementationContext();

  // Check if conditionals should be disabled (e.g., in Build mode)
  const isDisabled = Boolean(
    env.uiFlags?.disabled?.binding || env.uiFlags?.disabled?.expression?.(expr)
  );

  if (isDisabled) {
    return <DisabledConditionalSettings expr={expr} />;
  }

  return <EnabledConditionalSettings expr={expr} dispatch={dispatch} />;
}

function EnabledConditionalSettings({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
}) {
  const {
    type,
    env: {expressionPath},
  } = getAnn(expr);

  const getPathForDispatch: GetPathForDispatch = React.useCallback(
    function getPathForDispatch(nestedExpr: ContextualExpression) {
      return getAnn(nestedExpr).env.expressionPath.slice(expressionPath.length);
    },
    [expressionPath]
  );

  const configExpr = React.useMemo(() => getConfigurationExpr(expr), [expr]);

  const wfDispatch = useDispatch();

  const isMiniSettingsOpen = useDesignerSelector(selectIsMiniSettingsOpen);

  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  // Track whether cleanup has already run to prevent double execution
  const cleanupRanRef = React.useRef(false);

  const handleConfirmDelete = React.useCallback(() => {
    cleanupRanRef.current = true;
    dispatch(bindChangedAt(null, type, [], 'remove-conditional'));
    wfDispatch(conditionalClosed());
  }, [dispatch, type, wfDispatch]);

  const handleOpenFromMiniSettings = React.useCallback(() => {
    wfDispatch(conditionalOpenedAt({expressionPath}));
  }, [wfDispatch, expressionPath]);

  const autoOpenPath = useDesignerSelector(getAutoOpenConditionalPath);
  const {width: viewportWidth, height: viewportHeight} =
    useDesignerSelector(selectViewport);

  // need to call this here in case someone flips the feature flag mid-session
  const TOPBAR_HEIGHT = getTopBarHeight();

  /*
    The viewport used when rendering (legacy) popovers in conditional settings.

    Vertically the viewport box starts at the bottom of the top bar and extends
    to the bottom of the viewport.

    Horizontally the viewport box starts 8px from the right edge of the viewport
    (to account for padding) and extends CONDITIONAL_SETTINGS_WIDTH to the left.

    N.B! This assumes conditionals are rendered in the right sidebar. It will
    need to be updated if conditionals can be rendered elsewhere.
   */
  const conditionalSettingsViewportBox: Box = React.useMemo(
    () => ({
      left: viewportWidth - CONDITIONAL_SETTINGS_WIDTH - 8,
      top: TOPBAR_HEIGHT,
      width: CONDITIONAL_SETTINGS_WIDTH,
      height: viewportHeight - TOPBAR_HEIGHT,
    }),
    [viewportWidth, viewportHeight, TOPBAR_HEIGHT]
  );

  const shouldAutoOpen = autoOpenPath
    ? Expressions.pathsEqual(autoOpenPath, expressionPath)
    : false;

  // Sync controlled popover state with shouldAutoOpen
  React.useEffect(() => {
    if (shouldAutoOpen) {
      setIsPopoverOpen(true);
    }
  }, [shouldAutoOpen]);

  const handleCancelConfirmation = React.useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const handleDeleteClick = React.useCallback(() => {
    if (configExpr) {
      const analysis = analyzeConditionalForCleanup(configExpr);

      // Don't show the delete confirmation if the conditional is empty
      // This mimics the click away behavior
      if (analysis.status === 'empty') {
        handleConfirmDelete();
        return;
      }
    }
    setShowConfirmation(true);
  }, [configExpr, handleConfirmDelete]);

  // Store latest values in refs so cleanup can access them without causing effect re-runs
  // (wfDispatch is already stable from useDispatch, so no ref needed)
  const configExprRef = React.useRef(configExpr);
  const dispatchRef = React.useRef(dispatch);
  const typeRef = React.useRef(type);
  const getPathForDispatchRef = React.useRef(getPathForDispatch);

  React.useEffect(() => {
    configExprRef.current = configExpr;
    dispatchRef.current = dispatch;
    typeRef.current = type;
    getPathForDispatchRef.current = getPathForDispatch;
  });

  // Clean up incomplete conditions - called on popover close and component unmount
  const cleanupIncompleteConditions = React.useCallback(() => {
    if (cleanupRanRef.current) {
      return;
    }
    cleanupRanRef.current = true;

    const currentConfigExpr = configExprRef.current;
    if (currentConfigExpr) {
      const analysis = analyzeConditionalForCleanup(currentConfigExpr);
      if (analysis.status === 'incomplete') {
        // Dispatch at config path to preserve implicit wrappers like booleanToVisibilityConditions
        const configPath = getPathForDispatchRef.current(currentConfigExpr);
        dispatchRef.current(
          bindChangedAt(analysis.cleanedArg, typeRef.current, configPath)
        );
      } else if (analysis.status === 'empty') {
        dispatchRef.current(
          bindChangedAt(null, typeRef.current, [], 'remove-conditional')
        );
      }
    }

    wfDispatch(conditionalClosed());
  }, [wfDispatch]);

  const renderPopoverContent = React.useCallback(() => {
    return showConfirmation ? (
      <ConfirmationContent
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirmDelete}
      />
    ) : (
      <ConditionalSettingsContent
        configExpr={configExpr}
        type={type}
        dispatch={dispatch}
        getPathForDispatch={getPathForDispatch}
        onDeleteClick={handleDeleteClick}
      />
    );
  }, [
    configExpr,
    type,
    dispatch,
    getPathForDispatch,
    handleCancelConfirmation,
    handleConfirmDelete,
    handleDeleteClick,
    showConfirmation,
  ]);

  const handlePopoverChange = React.useCallback(
    (state: {open: boolean}) => {
      if (state.open) {
        // Reset cleanup flag when popover opens
        cleanupRanRef.current = false;
        setIsPopoverOpen(true);
        return;
      }

      if (showConfirmation) {
        setShowConfirmation(false);
        return;
      }

      cleanupIncompleteConditions();
      setIsPopoverOpen(false);
      setShowConfirmation(false);
    },
    [showConfirmation, cleanupIncompleteConditions]
  );

  // Trigger cleanup when component unmounts
  // This is required because the Popover onChange callback is not triggered when the component is unmounted.
  React.useEffect(() => {
    return () => {
      cleanupIncompleteConditions();
    };
  }, [cleanupIncompleteConditions]);

  if (!configExpr) {
    return null;
  }

  return (
    <Viewport box={conditionalSettingsViewportBox}>
      <DeprecatedPopoverBridge
        state={{open: isPopoverOpen}}
        renderPopoverContent={renderPopoverContent}
        showMask="TRANSPARENT"
        metricsIdentifier="conditional-settings-popover"
        showArrow={false}
        onChange={handlePopoverChange}
      >
        {({Anchor, openPopover}) => {
          const handleClick =
            isMiniSettingsOpen && !shouldKeepMiniSettingsOpen(type)
              ? handleOpenFromMiniSettings
              : openPopover;
          return (
            <>
              <Anchor asChild={false}>
                {(ref: React.RefObject<any>) => (
                  <Hitarea>
                    <SelectButton _ref={ref} dynamic onClick={handleClick}>
                      <Grid
                        alignItems="center"
                        // column ~> icon | label | icon
                        gridTemplateColumns="min-content minmax(0px, max-content)"
                        gridColumnGap={1}
                        gridAutoFlow="column"
                      >
                        <ConditionalRuleIcon />
                        <span>Conditional</span>
                      </Grid>
                    </SelectButton>
                  </Hitarea>
                )}
              </Anchor>
              <ConfigurationPreview
                configuration={configExpr}
                onPreviewClick={handleClick}
              />
            </>
          );
        }}
      </DeprecatedPopoverBridge>
    </Viewport>
  );
}

const noop = () => {};

function DisabledConditionalSettings({expr}: {expr: ContextualExpression}) {
  const configExpr = React.useMemo(() => getConfigurationExpr(expr), [expr]);
  if (!configExpr) {
    return null;
  }
  return (
    <>
      <Hitarea>
        <SelectButton dynamic disabled aria-disabled>
          <Grid
            alignItems="center"
            gridTemplateColumns="min-content minmax(0px, max-content)"
            gridColumnGap={1}
            gridAutoFlow="column"
          >
            <ConditionalRuleIcon />
            <span>Conditional</span>
          </Grid>
        </SelectButton>
      </Hitarea>
      <ConfigurationPreview
        isDisabled
        configuration={configExpr}
        onPreviewClick={noop}
      />
    </>
  );
}

function getConfigurationExpr(
  expr: ContextualExpression
): ContextualExpression | null {
  const call = ContextualExpressions.getCall(stripAnn(expr));

  if (!call) {
    return null;
  }

  // if expr is a conditional call, return its argument
  if (isConditionalReferenceCE(stripAnn(call.fun))) {
    return call.arg;
  }

  // conditional may be wrapped in implicit functions and we want to unwrap
  // them to find the conditional call expression and its argument
  return getConfigurationExpr(call.arg);
}

function selectViewport(state: DesignerStores): ViewportState {
  return state.ViewportStore;
}

const selectIsMiniSettingsOpen = createSelector(
  (state: DesignerStores) => state.UiStore.miniSettingsFlags,
  (miniSettingsFlags: Record<string, boolean>) =>
    Object.keys(miniSettingsFlags).length > 0
);

const PreviewWrapper = emotionStyled('div')(
  {
    marginTop: '$1',
    marginBottom: '$1',
    padding: '$1 $2',
    border: '$border1',
    borderRadius: '$2',
    color: '$textPrimary',
    // The preview might be in a EE grid, ensure that we take the full width always
    gridColumn: '1 / 3',
    overflowWrap: 'break-word',
  },
  {
    variants: {
      state: {
        enabled: {
          '&:hover': {
            backgroundColor: '$backgroundSecondary',
          },
        },
        disabled: {
          opacity: '$disabled',
        },
      },
    },
  }
);

const PreviewList = emotionStyled('ul')({
  padding: '0 $4',
  margin: 0,
});

const ConfigurationPreview = ({
  configuration,
  isDisabled,
  onPreviewClick,
}: {
  configuration: ContextualExpression;
  isDisabled?: boolean;
  onPreviewClick: (arg: unknown) => void;
}) => {
  const deconstructed = deconstructConfiguration(configuration);
  if (!deconstructed) {
    return null;
  }
  const {rules, fallbackValue} = deconstructed;

  const hideFallbackText =
    rules.length === 1 &&
    rules[0] &&
    shouldHideSimpleConditionalPreviewFallbackText(
      getAnn(fallbackValue).type,
      stripAll(rules[0].ruleOutputValue),
      stripAll(fallbackValue)
    );
  return (
    <PreviewWrapper
      state={isDisabled ? 'disabled' : 'enabled'}
      onClick={onPreviewClick}
      data-automation-id="conditional-preview"
    >
      {rules.map((rule, index) => (
        <View key={index}>
          <RuleGroupPreview
            ruleCondition={rule.ruleCondition}
            ruleOutput={rule.ruleOutputValue}
            automationId={`conditional-group-${index + 1}-preview`}
          />
          {hideFallbackText ? null : <Separator />}
        </View>
      ))}
      {hideFallbackText ? null : (
        <View>
          <View>
            <Text>Else</Text>
          </View>
          <View
            display="flex"
            data-automation-id="conditional-fallback-preview"
          >
            <ArrowSwooshDownRight />
            <View minWidth={0} flex={1}>
              <Text colorVariant="secondary">
                <DataTypeSummary context={fallbackValue} />
              </Text>
            </View>
          </View>
        </View>
      )}
    </PreviewWrapper>
  );
};

const RuleGroupPreview = ({
  ruleCondition,
  ruleOutput,
  automationId,
}: {
  ruleCondition: ContextualExpression;
  ruleOutput: ContextualExpression;
  automationId: string;
}) => {
  const callResult = ContextualExpressions.getCall(stripAnn(ruleCondition));
  const listExpr = callResult ? callResult.arg : null;
  const listResult = listExpr
    ? ContextualExpressions.getList(stripAnn(listExpr))
    : null;
  if (!listResult) {
    return null;
  }
  const conditionalMode = getConditionalMode(ruleCondition);

  const firstItem = listResult[0];

  if (!firstItem) {
    return null;
  }

  const renderItems =
    listResult.length > 1 ? (
      <>
        <Text data-automation-id="conditional-mode">
          If {conditionalMode} are true
        </Text>
        <PreviewList>
          {listResult.map((condition, index) => (
            <li key={index}>
              <RuleConditionPreview
                expr={condition}
                automationId={`group-rule-${index + 1}`}
              />
            </li>
          ))}
        </PreviewList>
      </>
    ) : (
      <RuleConditionPreview
        expr={firstItem}
        automationId="group-rule-1"
        onlyItem
      />
    );

  return (
    <View data-automation-id={automationId}>
      {renderItems}
      <View display="flex" data-automation-id="conditional-output">
        <ArrowSwooshDownRight />
        <View minWidth={0} flex={1}>
          <Text colorVariant="secondary">
            <DataTypeSummary context={ruleOutput} />
          </Text>
        </View>
      </View>
    </View>
  );
};

const RuleConditionPreview = ({
  expr,
  automationId,
  onlyItem,
}: {
  expr: ContextualExpression;
  automationId: string;
  onlyItem?: boolean;
}) => {
  const currentComponent = useDesignerSelector(selectCurrentComponent);
  const styleBlockState = useDesignerSelector(selectStyleBlockState);

  const envTypes = getAnn(expr).env.context.values;
  const strippedExpr = stripAll(expr);
  const call = Expressions.deconstructCall(strippedExpr);
  const [fieldArg, predicateArg] = call.args;

  const ctxCall = ContextualExpressions.deconstructCall(expr);

  const [ctxField, ctxPredicate] = ctxCall.args;

  if (!fieldArg || !predicateArg) {
    return null;
  }

  if (!ctxField || !ctxPredicate) {
    return null;
  }

  const operator = Expressions.getCall(fieldArg);

  const operatorText = operator
    ? getOperatorText(operator.arg, currentComponent, styleBlockState, envTypes)
    : '';

  const predicate = Expressions.getCall(predicateArg);
  const predicateVariable = Expressions.getVariable(predicateArg);

  const predicateFnText = predicate
    ? getBindingLabel(envTypes, predicate.fun)
    : predicateVariable
      ? getBindingLabel(envTypes, predicateArg)
      : '';

  const ctxPredicateCall = ContextualExpressions.getCall(
    stripAnn(ctxPredicate)
  );

  const mappedPredicateFn =
    predicateFnNameMap[predicateFnText] ?? predicateFnText;

  let previewText = `${operatorText} ${mappedPredicateFn}`;
  if (onlyItem) {
    previewText = `If ${previewText}`;
  }

  return (
    <Text data-automation-id={automationId}>
      {previewText}
      {ctxPredicateCall && (
        <span>
          {' '}
          <DataTypeSummary context={ctxPredicateCall.arg} />
        </span>
      )}
    </Text>
  );
};

/**
 * Resolves the text for the operator of a conditional rule.
 * If the operator is a slot binding, returns the slot name.
 * Otherwise, returns the binding label.
 * Slot names are stored differently compared to other bindings, so we need to handle them differently.
 */
function getOperatorText(
  operatorArg: Expression,
  currentComponent: Component.ComponentType,
  styleBlockState: StyleBlockStateType,
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>
): string {
  const fieldId = getFieldId(operatorArg);
  const maybeSlotLabel = resolveSlotBindingLabel(
    currentComponent,
    fieldId,
    styleBlockState
  );
  if (maybeSlotLabel) {
    return maybeSlotLabel;
  }
  return getBindingLabel(envTypes, operatorArg);
}

const predicateFnNameMap: Record<string, string> = {
  is: '=',
  'is not': '≠',
  'is before': '<',
  'is on or before': '≤',
  'is after': '>',
  'is on or after': '≥',
  'is greater than': '>',
  'is greater or equal': '≥',
  'is less than': '<',
  'is less or equal': '≤',
  'is any': '=',
  'is not any': '≠',
  'contains any': '=',
  'does not contain any': '≠',
  'count is': 'count =',
  'count is not': 'count ≠',
  'count is greater than': 'count >',
  'count is greater or equal': 'count ≥',
  'count is less than': 'count <',
  'count is less or equal': 'count ≤',
};
